import { useMutation, useQueryClient } from '@tanstack/react-query';

import { FulfillmentActionItemCode } from '@ekhodealer/ekho-common';

import type { MarkBackfillInventoryAddedToInventorySystemResponse } from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { apiClient } from '@config/server/apiClient';
import { ordersEndpoints } from '@config/server/apiEndpoints';

import { updateProgressCache } from './progressCacheUtils';


/**
 * Input type for marking backfill inventory as added to inventory system
 */
export interface MarkBackfillInventoryAddedToInventorySystemInput {
	orderId: string;
	purchaseOrderId: string;
}

/**
 * API function to mark backfill inventory as added to inventory system
 */
export const markBackfillInventoryAddedToInventorySystem = async (
	servicesProviderId: string,
	{ orderId, purchaseOrderId }: MarkBackfillInventoryAddedToInventorySystemInput,
): Promise<MarkBackfillInventoryAddedToInventorySystemResponse> => {
	const response = await apiClient.post<MarkBackfillInventoryAddedToInventorySystemResponse>(
		ordersEndpoints.markBackfillInventoryAddedToInventorySystem(servicesProviderId, orderId),
		{ purchaseOrderId },
	);
	return response.data;
};

/**
 * Hook for marking backfill inventory as added to inventory system
 * @param orderId - The purchased vehicle ID (orderId in frontend terms)
 */
export const useMarkBackfillInventoryAddedToInventorySystem = (orderId: string | undefined) => {
	const servicesProviderId = useCurrentServicesProviderId();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (input: { purchaseOrderId: string }) => {
			if (!orderId || !servicesProviderId) {
				throw new Error(
					'Unable to mark backfill inventory as added without an order ID or services provider ID.',
				);
			}

			return markBackfillInventoryAddedToInventorySystem(servicesProviderId, {
				orderId,
				purchaseOrderId: input.purchaseOrderId,
			});
		},
		onSuccess: (data) => {
			if (!orderId || !servicesProviderId) return;
			updateProgressCache(queryClient, servicesProviderId, orderId, data.progress, FulfillmentActionItemCode.MARK_BACKFILL_INVENTORY_ADDED_TO_INVENTORY_SYSTEM);
		},
	});
};
